import { IconName } from "./iconMapping";
import { TMessage, TMessageParam } from "../i18n/tt";
import { Tag, TagProps } from "./Tag";
import { FC, SyntheticEvent } from "react";
import { observer } from "mobx-react";
import {
  DCDomEventHandler,
  useDispatch,
  useDomEventHandler,
  useTranslation,
} from "../stores/RootStoreContext";
import { Popover } from "./Popover";
import { Box, VBox } from "./Box";
import { buttonRoleProps } from "../utils/events";
import { Icon, IconProps } from "./Icon";
import { LinkButton } from "./Button";
import { when } from "../utils/functions";
import { DCEvent } from "../events/eventsDomain";
import { renderIcon } from "./iconHelpers";

export type InfoTagDetailsItemProps = {
  id: string;
  icon: IconName;
  text: TMessage;
  onOpen?: DCEvent[] | (() => void);
  tag?: TagProps;
  variant?: "link" | "box";
};

export type InfoTagDetailsProps = {
  detailsHeading?: TMessage;
  details?: InfoTagDetailsItemProps[];
  variant?: "link" | "box";
};

export type InfoTagProps = {
  id: string;
  icon?: IconName | IconProps;
  text: TMessage | TMessageParam | string;
  detailsHeading?: TMessage;
  details?: InfoTagDetailsItemProps[];
  variant?: "link" | "box";
  tooltip?: TMessage;
  onClick?: DCDomEventHandler<SyntheticEvent<HTMLDivElement>, HTMLDivElement>;
};

export const InfoTagDetailsItem: FC<InfoTagDetailsItemProps> = observer((props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return !props.variant || props.variant === "link" ? (
    <Box key={props.id} alignItems={"center"} justifyContent={"space-between"} gap={"lg"}>
      <Box alignItems={"center"} flex={"none"}>
        <Icon name={props.icon} />
        {props.onOpen ? (
          <LinkButton label={props.text} onClick={props.onOpen} />
        ) : (
          <span>{t(props.text)}</span>
        )}
      </Box>
      {props.tag && (
        <Box justifyContent={"end"}>
          <Tag {...props.tag} />
        </Box>
      )}
    </Box>
  ) : (
    <Box
      key={props.id}
      alignItems={"center"}
      justifyContent={"space-between"}
      gap={"lg"}
      padding
      border
      rounded
      hoverBg={props.onOpen !== undefined}
      htmlProps={when(props.onOpen, (onOpen) => ({
        ...buttonRoleProps(() => (typeof onOpen === "function" ? onOpen() : dispatch(onOpen))),
      }))}
    >
      <Box alignItems={"center"} flex={"none"}>
        <Icon name={props.icon} />
        <span>{t(props.text)}</span>
      </Box>
      {props.tag && (
        <Box justifyContent={"end"}>
          <Tag {...props.tag} />
        </Box>
      )}
    </Box>
  );
});

export const InfoTagDetails: FC<InfoTagDetailsProps> = observer(
  ({ detailsHeading, details, variant = "link" }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        {detailsHeading && <span>{t(detailsHeading)}</span>}
        {details?.map((d) => (
          <InfoTagDetailsItem key={d.id} variant={variant} {...d} />
        ))}
      </VBox>
    );
  },
);

export const InfoTag: FC<InfoTagProps> = observer((props) => {
  const { tf } = useTranslation();
  const dispatch = useDispatch();
  const text = typeof props.text === "string" ? props.text : tf(props.text);
  const clickHandler = useDomEventHandler(props.onClick);

  if (props.details && props.details.length) {
    return (
      <Popover
        button={({ setReference, toggleOpen }) => {
          return (
            <Box
              ref={setReference}
              alignItems={"center"}
              border
              rounded
              bg
              fg={"subtle"}
              paddingX={"sm"}
              paddingY={"xs"}
              gap={"xs"}
              htmlProps={buttonRoleProps(toggleOpen)}
            >
              {props.icon && renderIcon(props.icon)}
              <small>{text}</small>
            </Box>
          );
        }}
        content={({ close }) => (
          <InfoTagDetails
            {...props}
            details={props.details?.map((d) => ({
              ...d,
              onOpen: when(d.onOpen, (onOpen) => () => {
                close();
                if (Array.isArray(onOpen)) {
                  dispatch(onOpen);
                } else {
                  onOpen?.();
                }
              }),
            }))}
          />
        )}
      />
    );
  }
  return (
    <Box
      alignItems={"center"}
      border
      rounded
      bg
      fg={"subtle"}
      paddingX={"sm"}
      paddingY={"xs"}
      gap={"xs"}
      tooltip={props.tooltip ?? props.detailsHeading}
      htmlProps={buttonRoleProps(clickHandler)}
    >
      {props.icon && renderIcon(props.icon)}
      <small>{text}</small>
    </Box>
  );
});
